import { Storage } from '../lib/storage';
import { applySerieResult } from '../engine/seriespillResults';

// ─────────────────────────────────────────────────────────────────────
// useSeriesActions — the seriespill (league) action handlers lifted verbatim
// out of Padel24AppMain (extracted from the former monolith) as a god-component
// collapse slice (PR-AY). recordSerieResultForTournament reads effectivePlayerDB (from
// useDerivations) + applySerieResult, so the hook is called AFTER useDerivations
// in Padel24AppMain. persistSeries / persistPausedSeries are private to the hook
// (internal-only). All writes are Storage-first (await Storage.set before
// setState). These handlers touch only the active/paused series state + their
// setters — NOT the R1 §13.20 loaded-flag refs (those stay in useSeries; the
// blanket-persist effects remain the sole flag-gated writers), and they only
// run on user action (after load) so they never write-before-load.
// ─────────────────────────────────────────────────────────────────────
export interface UseSeriesActionsDeps {
  activeSeries: any;
  setActiveSeries: (v: any) => void;
  pausedSeries: any;
  setPausedSeries: (v: any) => void;
  showToast: (kind: any, msg: any) => void;
  setView: (v: any) => void;
  effectivePlayerDB: any;
}

export function useSeriesActions({
  activeSeries,
  setActiveSeries,
  pausedSeries,
  setPausedSeries,
  showToast,
  setView,
  effectivePlayerDB,
}: UseSeriesActionsDeps) {
  const recordSerieResultForTournament = (updatedMatch: any) => {
    if (!activeSeries) return;
    updateActiveSeries(applySerieResult(activeSeries, updatedMatch, effectivePlayerDB));
  };

  // Persistence for the active series (its own track — independent of tournament/paused)
  const persistSeries = async (series: any) => {
    try {
      if (series == null) await Storage.set('active_series_v1', '');
      else await Storage.set('active_series_v1', JSON.stringify(series));
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre aktiv serie:', e);
    }
  };
  // Storage-first update of the series (HANDOVER rule: persist BEFORE setState)
  const updateActiveSeries = async (next: any) => {
    await persistSeries(next);
    setActiveSeries(next);
  };

  // Persist a serie match's IN-PROGRESS score (liveScore) continuously, WITHOUT
  // finalising it (no ELO, no result) — the live banepad score. Storage-first.
  const recordSerieLiveScore = (updatedMatch: any) => {
    if (!activeSeries) return undefined;
    return updateActiveSeries({
      ...activeSeries,
      matches: (activeSeries.matches || []).map((m: any) =>
        m.id === updatedMatch.id ? { ...m, liveScore: updatedMatch.liveScore } : m,
      ),
    });
  };

  // Persistence for paused series
  const persistPausedSeries = async (list: any) => {
    try {
      await Storage.set('paused_series_v1', JSON.stringify(list));
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre pausede serier:', e);
    }
  };

  // Pause the active series: move it to the pausedSeries list, remove it from the active slot.
  // The series keeps all its data (pairs, matches, standings, history). Storage-first:
  // write the paused list and clear the active slot in one atomic flow.
  const handlePauseSeries = async () => {
    if (!activeSeries) return;
    const snapshot = {
      ...activeSeries,
      pausedAt: Date.now(),
      // If the series lacks an id, give it one so we can re-find it later
      id: activeSeries.id || `series_paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    };
    const nextList = [snapshot, ...pausedSeries.filter((p) => p.id !== snapshot.id)];
    await persistPausedSeries(nextList);
    setPausedSeries(nextList);
    await updateActiveSeries(null);
    showToast('success', `Serie «${snapshot.config?.name || 'Seriespill'}» satt på pause`);
  };

  // Resume a paused series: move it back into the active slot, remove it from the paused list.
  // Requires that there is NOT already an active series (the UI should handle that).
  const handleResumeSeries = async (seriesId: any) => {
    if (activeSeries) {
      showToast('error', 'Du har allerede en aktiv serie. Pause den først.');
      return;
    }
    const target = pausedSeries.find((p) => p.id === seriesId);
    if (!target) return;
    const { pausedAt: _pausedAt, ...rest } = target;
    const nextList = pausedSeries.filter((p) => p.id !== seriesId);
    await persistPausedSeries(nextList);
    setPausedSeries(nextList);
    await updateActiveSeries(rest);
    setView('seriespill');
    showToast('success', `Serie «${rest.config?.name || 'Seriespill'}» gjenopptatt`);
  };

  // Permanently delete a paused series (without archiving matches — because the pause flow
  // has not committed ELO to the player DB). Used for cleanup.
  const handleDeletePausedSeries = async (seriesId: any) => {
    const nextList = pausedSeries.filter((p) => p.id !== seriesId);
    await persistPausedSeries(nextList);
    setPausedSeries(nextList);
    showToast('success', 'Pauset serie slettet');
  };

  return {
    recordSerieResultForTournament,
    recordSerieLiveScore,
    updateActiveSeries,
    handlePauseSeries,
    handleResumeSeries,
    handleDeletePausedSeries,
  };
}
